import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {applicationSelector} from '../../slices/application';
import {extractCanvasMeasurementsFromDesign} from '../../utils/utils';
import {useShadowRoot} from '../../contexts/ShadowRootContext';

const MINIMAP_HEIGHT = 92;
const MINIMAP_SIDE_MARGIN = 40;

// A scaled-down overview of the tube shown only when a slender tube overflows the
// viewport and scrolls horizontally. It renders the tube outline plus a marker per
// design object, and a box indicating which slice of the tube is currently in view.
// Click or drag it to jump to a position.
const TubeMinimap = () => {
  const shadowRoot = useShadowRoot();
  const {design} = useSelector(applicationSelector);
  const m = extractCanvasMeasurementsFromDesign(design);
  const [scroll, setScroll] = useState({left: 0, view: 0, content: 0});
  const draggingRef = useRef(false);

  // The horizontal scroll container is .hs-canvas on desktop. (On mobile the
  // wrapper scrolls instead and .hs-canvas is not scrollable, so the guard below
  // hides the minimap there.)
  const getScrollEl = () =>
    shadowRoot.getElementsByClassName?.('hs-canvas')?.[0] || shadowRoot.querySelector?.('.hs-canvas');

  useEffect(() => {
    const el =
      shadowRoot.getElementsByClassName?.('hs-canvas')?.[0] || shadowRoot.querySelector?.('.hs-canvas');
    if (!el) return undefined;

    let ticking = false;
    const update = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScroll({left: el.scrollLeft, view: el.clientWidth, content: el.scrollWidth});
        ticking = false;
      });
    };
    update();

    el.addEventListener('scroll', update, {passive: true});
    window.addEventListener('resize', update);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      if (ro) ro.disconnect();
    };
  }, [shadowRoot, m.isOverflow, m.canvasWidth]);

  // Only render when the tube actually overflows the visible viewport.
  if (!m.isOverflow || scroll.view === 0 || scroll.content <= scroll.view + 1) {
    return null;
  }

  const contentWidth = m.canvasWidth || 1;
  const contentHeight = m.canvasHeight || 1;
  // Fixed height; the width scales with the tube's aspect ratio so the minimap
  // never changes height as the tube length changes. Cap the width to the
  // available space (only then does height shrink, for an extremely long tube).
  const aspect = contentWidth / contentHeight;
  const maxWidth = Math.max(120, scroll.view - MINIMAP_SIDE_MARGIN);
  let height = MINIMAP_HEIGHT;
  let width = height * aspect;
  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspect;
  }
  const tubeLeft = m.padding + m.inset;
  const tubeTop = (contentHeight - m.tubeHeight) / 2;

  // The scroll container's px map 1:1 to the drawing's coordinate width; scale to
  // the minimap's coordinate space in case the two differ slightly (scrollbars).
  const domToContent = contentWidth / scroll.content;
  const viewX = scroll.left * domToContent;
  const viewW = scroll.view * domToContent;

  const objects = design.objects ?? [];
  const minObjectSize = m.tubeHeight * 0.12;

  const scrollToClientX = (clientX, svgEl) => {
    const el = getScrollEl();
    if (!el) return;
    const rect = svgEl.getBoundingClientRect();
    if (rect.width === 0) return;
    const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    el.scrollLeft = fraction * scroll.content - scroll.view / 2;
  };

  return (
    <div className="hs-canvas-minimap">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${contentWidth} ${contentHeight}`}
        preserveAspectRatio="none"
        onMouseDown={(e) => {
          draggingRef.current = true;
          scrollToClientX(e.clientX, e.currentTarget);
        }}
        onMouseMove={(e) => {
          if (draggingRef.current) scrollToClientX(e.clientX, e.currentTarget);
        }}
        onMouseUp={() => {
          draggingRef.current = false;
        }}
        onMouseLeave={() => {
          draggingRef.current = false;
        }}>
        <rect
          className="hs-minimap-tube"
          x={tubeLeft}
          y={tubeTop}
          width={m.tubeWidth}
          height={m.tubeHeight}
          rx={m.tubeHeight / 2}
          ry={m.tubeHeight / 2}
        />
        {objects.map((o, i) => {
          const scaleX = Number(o.scaleX) || 0;
          const scaleY = Number(o.scaleY) || 0;
          // Objects are anchored by a per-object offset (usually their center), so
          // the stored (x, y) is NOT the top-left. Replicate the canvas transform
          // (anchor - scaled offset) to recover the visual center, matching how
          // TextObject/ImageObject render on the real canvas.
          const offFracX = o.width ? (Number(o.offsetX) || 0) / o.width : 0;
          const offFracY = o.height ? (Number(o.offsetY) || 0) / o.height : 0;
          const centerX =
            tubeLeft + ((Number(o.x) || 0) - offFracX * scaleX + Math.abs(scaleX) / 2) * m.tubeWidth;
          const centerY =
            tubeTop + ((Number(o.y) || 0) - offFracY * scaleY + Math.abs(scaleY) / 2) * m.tubeHeight;
          const w = Math.max(minObjectSize, Math.abs(scaleX) * m.tubeWidth);
          const h = Math.max(minObjectSize, Math.abs(scaleY) * m.tubeHeight);
          return (
            <rect
              key={o.id ?? i}
              className="hs-minimap-object"
              x={centerX - w / 2}
              y={centerY - h / 2}
              width={w}
              height={h}
            />
          );
        })}
        <rect
          className="hs-minimap-viewport"
          x={viewX}
          y={0}
          width={viewW}
          height={contentHeight}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
};

export default TubeMinimap;
